import { AdminPageCabecalhoComponent } from '../../shared/admin-page-cabecalho/admin-page-cabecalho.component';
import { ChangeDetectorRef, Component, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { combineLatest, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CeiSnackBarService } from '@cei-ufg/ngx-arquitetura-cei-frontend';
import { ListRetrieveUsuario, UsuariosApiService } from '@cei-ufg/ngx-cei-seguranca';
import { SessionContextService } from '@cei-ufg/ngx-cei-sso-client';
import { PessoaUsuarioApiService, PessoaComUsuario } from '../../services/pessoa-usuario-api.service';
import { PessoaPhotoService } from '../../services/pessoa-photo.service';
import { PessoaUsuarioFormComponent } from '../../shared/pessoa-usuario-form/pessoa-usuario-form.component';
import { emptyPessoaUsuarioForm } from '../../shared/pessoa-usuario-form/pessoa-usuario-form.model';
import {
  aplicarPermissoesGruposUsuarioNoForm,
  buildPessoaUsuarioPayload,
  carregarUsuarioAutorizacao,
  hasUsuarioData,
  mapListRetrieveUsuarioToForm,
  mapPessoaComUsuarioToForm,
  montarPutUsuarioWithRelations,
  validarContaUsuarioOpcional,
} from '../../shared/pessoa-usuario-form/pessoa-usuario-form.utils';
import { parseRouteNumericId } from '../../shared/admin-record.utils';
import {
  notifyAdminFormError,
  notifyAdminFormSuccess,
  stopIfAdminFormInvalid,
} from '../../shared/admin-form.utils';

type ResultadoCargaEdicao =
  | {
      modo: 'editar';
      record: PessoaComUsuario & { pessoaId: number };
      usuario: ListRetrieveUsuario | null;
      /** Quando veio de `?usuarioId=` mas já havia Pessoa — limpa a URL. */
      consolidarUrlPessoaId?: number;
    }
  | { modo: 'criar'; usuario: ListRetrieveUsuario };

@Component({
  selector: 'cei-admin-pessoa-edit',
  standalone: true,
  imports: [AdminPageCabecalhoComponent, CommonModule, RouterModule, PessoaUsuarioFormComponent],
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss'],
})
export class AdminPessoaEditComponent implements OnDestroy {
  form = emptyPessoaUsuarioForm();
  /** Incrementado ao hidratar o formulário para remontar autocompletes com chips do backend. */
  formVersao = 0;
  pessoaId = 0;
  pendingFoto?: File;
  removeFotoOnSave = false;

  private readonly api = inject(PessoaUsuarioApiService);
  private readonly usuariosApi = inject(UsuariosApiService);
  private readonly sessionContext = inject(SessionContextService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly snackBar = inject(CeiSnackBarService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly pessoaPhotoService = inject(PessoaPhotoService);
  /** Usuário retornado pela API de autorização; fonte de permissões/grupos do formulário. */
  private usuarioAutorizacao: ListRetrieveUsuario | null = null;

  /** Título do cabeçalho conforme exista Pessoa vinculada. */
  get tituloPagina(): string {
    return this.pessoaId > 0 ? 'Editar usuário' : 'Cadastrar dados pessoais';
  }

  ngOnInit(): void {
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        switchMap(([params, query]) => {
          const pessoaId = parseRouteNumericId(params.get('id'));
          const usuarioId = Number(query.get('usuarioId') || 0);
          const loginQuery = (query.get('login') || '').trim();

          if (pessoaId) {
            return this.api.resolveAdminRecord(pessoaId).pipe(
              switchMap((record) => {
                if (!record) {
                  return of(null);
                }

                const login = record.usuario?.login ?? '';

                return carregarUsuarioAutorizacao(this.usuariosApi, login).pipe(
                  map(
                    (usuario): ResultadoCargaEdicao => ({
                      modo: 'editar',
                      record,
                      usuario,
                    }),
                  ),
                );
              }),
            );
          }

          // Preferir login (estável entre domínio local e API de autorização remota).
          if (loginQuery) {
            return carregarUsuarioAutorizacao(this.usuariosApi, loginQuery).pipe(
              map((usuario) => {
                if (!usuario) {
                  return null;
                }
                return { modo: 'criar' as const, usuario };
              }),
            );
          }

          if (usuarioId > 0) {
            return this.usuariosApi
              .usuarioRetrieve({ id: usuarioId }, 'body', false, { transferCache: false })
              .pipe(
                switchMap((usuarioBase) => {
                  const pessoaIdVinculada =
                    typeof usuarioBase.pessoa_id === 'number' && usuarioBase.pessoa_id > 0
                      ? usuarioBase.pessoa_id
                      : null;

                  // Conta já tem Pessoa: carrega dados pessoais (não só o usuário).
                  if (pessoaIdVinculada != null) {
                    return this.api.resolveAdminRecord(pessoaIdVinculada).pipe(
                      switchMap((record) => {
                        if (!record) {
                          return of(null);
                        }
                        const login = record.usuario?.login ?? usuarioBase.login;
                        return carregarUsuarioAutorizacao(this.usuariosApi, login).pipe(
                          map(
                            (usuario): ResultadoCargaEdicao => ({
                              modo: 'editar',
                              record,
                              usuario: usuario ?? usuarioBase,
                              consolidarUrlPessoaId: pessoaIdVinculada,
                            }),
                          ),
                        );
                      }),
                    );
                  }

                  return carregarUsuarioAutorizacao(this.usuariosApi, usuarioBase.login).pipe(
                    map(
                      (usuario): ResultadoCargaEdicao => ({
                        modo: 'criar',
                        usuario: usuario ?? usuarioBase,
                      }),
                    ),
                  );
                }),
              );
          }

          return of(null);
        }),
      )
      .subscribe({
        next: (resultado) => {
          if (!resultado) {
            this.resetFormState();
            return;
          }

          if (resultado.modo === 'editar') {
            const { record, usuario, consolidarUrlPessoaId } = resultado;
            this.pessoaId = record.pessoaId;
            this.form = mapPessoaComUsuarioToForm(record);
            this.usuarioAutorizacao = usuario;
            if (usuario) {
              aplicarPermissoesGruposUsuarioNoForm(this.form, usuario);
            }
            this.formVersao += 1;
            this.loadFormPhotoPreview(this.pessoaId, record.foto_url);
            this.sincronizarPessoaIdNaSessaoSeProprioUsuario(record);
            if (consolidarUrlPessoaId) {
              void this.router.navigate(['/admin/pessoas/atualizar', consolidarUrlPessoaId], {
                replaceUrl: true,
              });
            }
          } else {
            this.pessoaId = 0;
            this.form = mapListRetrieveUsuarioToForm(resultado.usuario);
            this.usuarioAutorizacao = resultado.usuario;
            aplicarPermissoesGruposUsuarioNoForm(this.form, resultado.usuario);
            this.formVersao += 1;
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.resetFormState();
          notifyAdminFormError(this.snackBar, 'Erro ao carregar registro.', err);
        },
      });
  }

  onSubmit(form: NgForm) {
    if (stopIfAdminFormInvalid(form)) {
      this.cdr.markForCheck();
      return;
    }

    const criando = this.pessoaId <= 0;
    if (criando) {
      const erroConta = validarContaUsuarioOpcional(this.form, 'create');
      if (erroConta) {
        this.snackBar.error(erroConta);
        this.cdr.markForCheck();
        return;
      }
    }
    const payload = buildPessoaUsuarioPayload(
      this.form,
      hasUsuarioData(this.form) || !!this.form.login?.trim(),
    );

    const requisicao$ = criando
      ? this.api.createWithUsuario(payload)
      : this.api.updateWithUsuario(this.pessoaId, payload);

    requisicao$
      .pipe(
        switchMap((pessoa) =>
          this.salvarPermissoesGruposUsuario(pessoa).pipe(
            catchError((err) => {
              notifyAdminFormError(
                this.snackBar,
                criando
                  ? 'Pessoa criada, mas falhou ao salvar permissões do usuário.'
                  : 'Pessoa atualizada, mas falhou ao salvar permissões do usuário.',
                err,
              );
              return of(pessoa);
            }),
          ),
        ),
      )
      .subscribe({
        next: (pessoa) => {
          this.pessoaId = pessoa.id;
          this.sincronizarPessoaIdNaSessaoSeProprioUsuario(pessoa);
          const previewAtual = this.form.fotoUrl;
          this.form = mapPessoaComUsuarioToForm(pessoa);
          this.recarregarPermissoesGruposUsuarioAposSalvar(
            pessoa,
            previewAtual,
            criando ? 'Pessoa criada com sucesso!' : 'Pessoa atualizada com sucesso!',
          );
          if (criando && pessoa.id) {
            void this.router.navigate(['/admin/pessoas/atualizar', pessoa.id], {
              replaceUrl: true,
            });
          }
        },
        error: (err) => {
          notifyAdminFormError(
            this.snackBar,
            criando ? 'Erro ao criar pessoa.' : 'Erro ao atualizar pessoa.',
            err,
          );
          this.cdr.markForCheck();
        },
      });
  }

  onFotoSelected(file: File): void {
    this.pendingFoto = file;
    this.removeFotoOnSave = false;
  }

  onFotoRemoved(): void {
    this.pendingFoto = undefined;
    this.removeFotoOnSave = true;
  }

  private resetFormState(): void {
    this.form = emptyPessoaUsuarioForm();
    this.formVersao = 0;
    this.pessoaId = 0;
    this.pendingFoto = undefined;
    this.removeFotoOnSave = false;
    this.usuarioAutorizacao = null;
  }

  /** Atualiza `pessoaId` na sessão quando o registro salvo é da conta logada. */
  private sincronizarPessoaIdNaSessaoSeProprioUsuario(pessoa: PessoaComUsuario): void {
    const usuarioSalvoId = pessoa.usuario?.id ?? this.usuarioAutorizacao?.id;
    const usuarioSessaoId = this.sessionContext.userId();
    if (usuarioSalvoId == null || usuarioSessaoId == null || usuarioSalvoId !== usuarioSessaoId) {
      return;
    }
    this.sessionContext.patchDetails({ pessoa_id: pessoa.id });
  }

  /** Persiste permissões/grupos via UsuariosApiService, após salvar dados da pessoa. */
  private salvarPermissoesGruposUsuario(pessoa: PessoaComUsuario) {
    if (!this.usuarioAutorizacao) {
      return of(pessoa);
    }

    return this.usuariosApi
      .usuarioComRelacoesUpdate({
        id: this.usuarioAutorizacao.id,
        putUsuarioWithRelations: montarPutUsuarioWithRelations(this.form, this.usuarioAutorizacao),
      })
      .pipe(map(() => pessoa));
  }

  /** Recarrega permissões/grupos da API de autorização após salvar. */
  private recarregarPermissoesGruposUsuarioAposSalvar(
    pessoa: PessoaComUsuario,
    previewAtual?: string | null,
    successMessage = 'Pessoa atualizada com sucesso!',
  ): void {
    const finalizar = (): void => {
      if (previewAtual && pessoa.foto_url && !this.pendingFoto && !this.removeFotoOnSave) {
        this.form.fotoUrl = previewAtual;
      }
      this.formVersao += 1;
      this.persistFotoChanges(successMessage, pessoa.foto_url);
    };

    const login = this.form.login?.trim();
    if (!login) {
      finalizar();
      return;
    }

    carregarUsuarioAutorizacao(this.usuariosApi, login).subscribe({
      next: (usuario) => {
        if (usuario) {
          this.usuarioAutorizacao = usuario;
          aplicarPermissoesGruposUsuarioNoForm(this.form, usuario);
        }
        finalizar();
      },
      error: () => {
        notifyAdminFormError(
          this.snackBar,
          'Pessoa salva, mas falhou ao recarregar permissões do usuário.',
        );
        finalizar();
      },
    });
  }

  ngOnDestroy(): void {
    if (this.pessoaId) {
      this.pessoaPhotoService.revoke(this.formPhotoCacheKey(this.pessoaId));
    }
  }

  private persistFotoChanges(successMessage: string, hasRemotePhoto?: string | null): void {
    const refreshToolbarPhoto = (fotoUrl: string | null | undefined): void => {
      const myPessoaId = this.sessionContext.pessoaId();
      if (myPessoaId !== this.pessoaId) {
        return;
      }

      this.sessionContext.patchDetails({ foto_url: fotoUrl ?? null });
    };

    const refreshFormPhoto = (fotoUrl?: string | null): void => {
      this.pendingFoto = undefined;
      this.removeFotoOnSave = false;
      this.loadFormPhotoPreview(this.pessoaId, fotoUrl ?? hasRemotePhoto);
    };

    if (this.removeFotoOnSave) {
      this.api.deleteFoto(this.pessoaId).subscribe({
        next: () => {
          this.form.fotoUrl = null;
          this.pendingFoto = undefined;
          this.removeFotoOnSave = false;
          this.pessoaPhotoService.revoke(this.formPhotoCacheKey(this.pessoaId));
          refreshToolbarPhoto(null);
          notifyAdminFormSuccess(this.snackBar, successMessage);
          this.cdr.markForCheck();
        },
        error: (err) => {
          notifyAdminFormError(this.snackBar, 'Dados salvos, mas falhou ao remover a foto.', err);
          this.cdr.markForCheck();
        },
      });
      return;
    }

    if (this.pendingFoto) {
      this.api.uploadFoto(this.pessoaId, this.pendingFoto).subscribe({
        next: (pessoa) => {
          refreshToolbarPhoto(pessoa.foto_url);
          refreshFormPhoto(pessoa.foto_url);
          notifyAdminFormSuccess(this.snackBar, successMessage);
          this.cdr.markForCheck();
        },
        error: (err) => {
          notifyAdminFormError(this.snackBar, 'Dados salvos, mas falhou o upload da foto.', err);
          this.cdr.markForCheck();
        },
      });
      return;
    }

    if (hasRemotePhoto) {
      refreshFormPhoto(hasRemotePhoto);
    }

    notifyAdminFormSuccess(this.snackBar, successMessage);
    this.cdr.markForCheck();
  }

  private loadFormPhotoPreview(pessoaId: number, fotoUrl?: string | null): void {
    if (!fotoUrl) {
      this.form.fotoUrl = null;
      this.pessoaPhotoService.revoke(this.formPhotoCacheKey(pessoaId));
      return;
    }

    this.pessoaPhotoService.fetchAuthenticatedPhotoUrl(pessoaId, this.formPhotoCacheKey(pessoaId)).subscribe({
      next: (objectUrl) => {
        this.form.fotoUrl = objectUrl ?? null;
        this.cdr.markForCheck();
      },
    });
  }

  private formPhotoCacheKey(pessoaId: number): string {
    return `edit-form-${pessoaId}`;
  }
}
